import { Component, h, Host, Element, Watch, Event, EventEmitter } from '@stencil/core';
import { AhaEzTabs } from '../aha-ez-tabs/aha-ez-tabs';
import { AhaEzTab } from '../aha-ez-tab/aha-ez-tab';

export interface AhaEzTabPanel extends HTMLAhaEzTabPanelElement {}

@Component({
  tag: 'aha-ez-tab-panel',
  styleUrl: 'aha-ez-tab-panel.css',
  shadow: true,
})
export class AhaEzTabPanel {
  @Element() host!: HTMLAhaEzTabPanelElement;

  //  Stencil supports watching on any property, but Stencil doesn't allow us to create a Prop decorator for native props. Since an element's `id` is a native prop, we can't create a `@Prop()` for it, but we can listen for changes to it.
  // eslint-disable-next-line @stencil/no-unused-watch
  @Watch('id')
  setAriaLabelledBy() {
    // Validates that the tab panel has an id attribute that ends with "-panel".
    if (!this.host.id.endsWith('-panel')) {
      console.error(
        `AhaEzTabPanel elements must have an id attribute that starts with the associated tab's id value and ends with "-panel".`,
        this.host,
      );
    }

    // When the id of the panel changes, update the `aria-labelledby` attribute of the panel.
    this.host.setAttribute('aria-labelledby', this.tabId);
  }

  // Stencil supports watching on any property, but Stencil doesn't allow us to create a Prop decorator for native props. Since an element's `ariaHidden` is a native prop, we can't create a `@Prop()` for it, but we can listen for changes to it.
  // eslint-disable-next-line @stencil/no-unused-watch
  @Watch('aria-hidden')
  ariaHiddenChanged() {
    const value = this.host.ariaHidden === 'true';
    this.host.tabIndex = value ? -1 : 0;

    // Emit an event when the panel is shown or hidden.
    value
      ? this.selectedEvent.emit({ panel: this.host })
      : this.deselectedEvent.emit({ panel: this.host });
  }

  get tabId() {
    return this.host.id.replace(/-panel$/, '');
  }

  get tab() {
    if (!this.host.id) return;

    // Find the tab within the tabs container (if it exists); otherwise find it in the document.
    // This way, even if there is an id collision, the proper tab will still be found and used.
    const tabId = this.tabId;
    const tab =
      this.tabsContainer?.tabs?.find(tab => tab.id === tabId) || document.getElementById(tabId);
    if (tab instanceof AhaEzTab) return tab;
  }

  get hidden() {
    return this.host.ariaHidden === 'true';
  }

  set hidden(value: boolean) {
    this.host.ariaHidden = value.toString();
  }

  get selected() {
    return Boolean(this.tab?.selected);
  }

  set selected(value: boolean) {
    const tab = this.tab;
    if (tab) tab.selected = value;
  }

  // Get the parent `aha-ez-tabs` element, if present.
  get tabsContainer() {
    const ezTabsRoot = this.host.assignedSlot?.getRootNode() as ShadowRoot;
    const ezTabsElem = ezTabsRoot?.host;
    if (ezTabsElem && ezTabsElem instanceof AhaEzTabs) {
      return ezTabsElem;
    }
  }

  // Show the panel when the tab is selected.
  // Should be called in `connectedCallback` and `componentDidLoad` to ensure that it always matches the tab's selected state, no matter when called in the lifecycle of the panel.
  private showPanelWhenTabSelected() {
    if (this.tab) {
      this.hidden = !this.tab.selected;
    }
  }

  connectedCallback() {
    this.showPanelWhenTabSelected();
  }

  componentDidLoad() {
    this.setAriaLabelledBy();
    this.showPanelWhenTabSelected();
  }

  @Event({
    eventName: 'panelSelected',
    cancelable: true,
    bubbles: true,
  }) //eslint-disable-line @stencil/decorators-style
  selectedEvent!: EventEmitter;

  @Event({
    eventName: 'panelDeslected',
    cancelable: true,
    bubbles: true,
  }) //eslint-disable-line @stencil/decorators-style
  deselectedEvent!: EventEmitter;

  render() {
    return (
      <Host aria-hidden="true" tabindex="-1" role="tabpanel">
        <slot></slot>
      </Host>
    );
  }
}
